import { makeStyles, TextField } from "@material-ui/core";
import { FC, useEffect, useRef, useState } from "react";
import {
  Edit,
  EditProps,
  email,
  FormGroupContextProvider,
  FormWithRedirect,
  minLength,
  minValue,
  NumberInput,
  PasswordInput,
  ReferenceArrayInput,
  required,
  SelectArrayInput,
  SelectInput,
  TextInput,
  useLoading,
} from "react-admin";
import { useSelector } from "react-redux";
import AreaOfAdvertising from "../../components/AreaOfAdvertising";

import CustomCard from "../../components/CustomCard";
import CustomSelect from "../../components/CustomSelect";
import CustomToolbar from "../../components/CustomToolbar";
import FormWrapper from "../../components/FormWrapper";
import ImageUploadInput from "../../components/ImageUploadInput";
import Spacing from "../../components/Spacing";
import { QATAR_CODE_PREFIX } from "../../constants/general";
import useDisabledOnLoading from "../../hooks/useDisabledOnLoading";
import firebaseConfig from "../../init/config";
import { firestore } from "../../init/firebase";
import { GenericObject } from "../../types/generic.types";
import { FormGroupNames } from "../../types/reactAdmin.types";
import { ReduxState } from "../../types/redux.types";
import { AppResource } from "../../types/resource.types";
import { isExists, isPhoneNumber, validateFileSize } from "../../utils/validators";
import { createCourtMinSlotTime } from "../Courts/utils";
import SocialMediaLinks from "../Gym/components/SocialMediaLinks";
import PhonePrefixCode from "./components/PhonePrefixCode";
import Coaches from "./utils/useCoachesLocalizations";

const useStyles = makeStyles({
  root: {
    "& .MuiChip-root": {
      height: "auto",
      backgroundColor: "inherit",
      margin: "0",
    },
    "& label": {
      backgroundColor: "#fff",
    },
  },
});

const courtMinSlotTime = createCourtMinSlotTime(10);

const CustomForm: FC<CustomEditProps> = (props) => {
  const classes = useStyles();
  const coach = props.record;
  coach.phone = coach.phone.replace(QATAR_CODE_PREFIX, "");
  coach.whatsapp = coach.whatsapp.replace(QATAR_CODE_PREFIX, "");
  const { socialsRef, minReservationSlotRef, areaOfAdvertisingRef, minSlotRef, emailRef } = props;

  const settings = useSelector<ReduxState, Record<string, any>>((state) => state.settings);

  const coachesQuery = (key: string, value: string) =>
    firestore.collection(AppResource.coaches).where(key, "==", value).limit(1);

  const loading = useLoading();
  const { disabledStyles } = useDisabledOnLoading(loading);
  const localizations = Coaches(props);

  const [socialLinksError, setSocialLinksError] = useState(false);
  const [socials, setSocials] = useState(coach.socials);
  const [minSlot, setMinSlot] = useState(coach.defaultSlotTime.toString());
  const [minReservationSlot, setMinReservationSlot] = useState(coach.minReservationSlot.toString());
  const [minReservationSlotsList, setMinReservationSlotsList] = useState<any[]>([]);

  const handleSelectMinReservationSlot = (selectedItem: string) => {
    setMinReservationSlot(selectedItem.toString());
    minReservationSlotRef.current = selectedItem;
  };

  const [emailError, setEmailError] = useState<boolean | string>("");
  const [enteredEmail, setEnteredEmail] = useState(coach.email);

  const emailHandler = async (e: any) => {
    const value = e.target.value;
    setEnteredEmail(value);
    if (value.trim() && value === coach.email) {
      setEmailError("");
      emailRef.current = coach.email;
    } else if (value.trim()) {
      const regex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
      if (regex.test(value)) {
        const urlencoded = new URLSearchParams();
        urlencoded.append("email", value);
        const response = await fetch(`${firebaseConfig.cloudFunctionsBaseUrl}/functions-checkingAuth`, {
          method: "post",
          body: urlencoded,
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        });
        if (response.status === 200) {
          emailRef.current = value;
          setEmailError("");
        } else {
          setEmailError("Email already exists");
        }
      } else {
        setEmailError("Invalid email");
      }
    } else {
      setEmailError(true);
    }
  };

  useEffect(() => {
    emailRef.current = coach.email;
  }, []);

  useEffect(() => {
    areaOfAdvertisingRef.current = coach.areaOfAdvertising;
  }, []);

  useEffect(() => {
    socialsRef.current = socials;
  }, [socials]);

  useEffect(() => {
    minSlotRef.current = coach.defaultSlotTime.toString();
  }, [coach.defaultSlotTime]);

  useEffect(() => {
    minReservationSlotRef.current = minReservationSlot;
  }, [minReservationSlot]);

  useEffect(() => {
    if (settings && settings.general) {
      const settingsMinReservationSlot = settings.general.maxReservationSlots;
      const list: GenericObject[] = [];
      for (let i = 0; i < settingsMinReservationSlot; i++) {
        list.push({ id: i + 1, name: i + 1 });
      }
      setMinReservationSlotsList(list);
    }
  }, [settings]);

  return (
    <FormWrapper>
      <FormGroupContextProvider name={FormGroupNames.coachesEdit}>
        <FormWithRedirect
          {...props}
          redirect={`/${AppResource.coaches}`}
          render={(formProps) => {
            return (
              <form onSubmit={formProps.handleSubmit} style={disabledStyles}>
                <CustomCard title="Details">
                  <Spacing>
                    {localizations?.[0]}
                    {localizations?.[1]}
                  </Spacing>

                  <Spacing>
                    {/* <TextInput
                      source="email"
                      variant="outlined"
                      fullWidth
                      validate={[
                        required(),
                        email(),
                        isExists({ query: coachesQuery, type: "email", currentValue: coach.email }),
                      ]}
                    /> */}

                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      onChange={emailHandler}
                      style={{ marginTop: 8 }}
                      label="Email"
                      helperText={enteredEmail.length && emailError ? emailError : ""}
                      error={!!enteredEmail && !!emailError}
                      defaultValue={enteredEmail}
                    />

                    <PasswordInput
                      source="password"
                      variant="outlined"
                      type="password"
                      fullWidth
                      validate={minLength(8)}
                    />
                  </Spacing>

                  <Spacing>
                    <TextInput
                      source="phone"
                      variant="outlined"
                      fullWidth
                      InputProps={{
                        startAdornment: <PhonePrefixCode />,
                      }}
                      validate={[
                        required(),
                        isExists({
                          query: coachesQuery,
                          type: "phone",
                          prefix: QATAR_CODE_PREFIX,
                          currentValue: coach.phone,
                        }),
                        isExists({
                          query: coachesQuery,
                          type: "whatsapp",
                          prefix: QATAR_CODE_PREFIX,
                          currentValue: coach.phone,
                        }),
                        isPhoneNumber(),
                      ]}
                    />

                    <TextInput
                      source="whatsapp"
                      variant="outlined"
                      fullWidth
                      InputProps={{
                        startAdornment: <PhonePrefixCode />,
                      }}
                      validate={[
                        required(),
                        isExists({
                          query: coachesQuery,
                          type: "whatsapp",
                          prefix: QATAR_CODE_PREFIX,
                          currentValue: coach.whatsapp,
                        }),
                        isExists({
                          query: coachesQuery,
                          type: "phone",
                          prefix: QATAR_CODE_PREFIX,
                          currentValue: coach.whatsapp,
                        }),
                        isPhoneNumber("whatsapp"),
                      ]}
                    />
                  </Spacing>

                  <Spacing>
                    {localizations?.[2]}
                    {localizations?.[3]}
                  </Spacing>
                  <Spacing>
                    <CustomSelect
                      selectedItem={minSlot.toString()}
                      handleSelectItem={(selectedItem: string) => {
                        minSlotRef.current = selectedItem;
                        setMinSlot(selectedItem.toString());
                      }}
                      labelTitle="Default slot time"
                      listItems={courtMinSlotTime}
                    />
                    <CustomSelect
                      selectedItem={minReservationSlot}
                      handleSelectItem={handleSelectMinReservationSlot}
                      labelTitle="Min Reservation Slot"
                      listItems={minReservationSlotsList}
                    />
                  </Spacing>
                  <Spacing>
                    <SelectInput
                      source="status"
                      choices={[
                        { id: "disabled", name: "Disabled" },
                        { id: "enabled", name: "Enabled" },
                      ]}
                      variant="outlined"
                      validate={[required()]}
                      fullWidth
                      label="Status"
                    />

                    <NumberInput
                      source="pricePerHour"
                      validate={[required(), minValue(0)]}
                      variant="outlined"
                      fullWidth
                      label="Price per hour"
                    />
                  </Spacing>

                  <ReferenceArrayInput
                    label="Sport Type"
                    source="sportTypeIds"
                    reference={AppResource.sportTypes}
                    variant="outlined"
                    validate={required()}
                    fullWidth
                  >
                    <SelectArrayInput optionText="title.en" size="small" className={classes.root} />
                  </ReferenceArrayInput>
                </CustomCard>

                <CustomCard title="Social media links">
                  <SocialMediaLinks
                    handleSocialLinks={(socialLinks: any) => setSocials(socialLinks)}
                    handleSocialLinksError={(value: boolean) => setSocialLinksError(value)}
                    defaultValue={coach.socials}
                  />
                </CustomCard>

                <CustomCard title="Picture *">
                  <ImageUploadInput source="picture" validate={[required(), validateFileSize("picture")]} />
                </CustomCard>

                <CustomCard title="Certificates *">
                  <ImageUploadInput
                    source="certificates"
                    validate={[required(), validateFileSize("certificates")]}
                    multiple={true}
                  />
                </CustomCard>

                <CustomCard title="Images *">
                  <ImageUploadInput
                    source="images"
                    validate={[required(), validateFileSize("images")]}
                    multiple={true}
                  />
                </CustomCard>

                <CustomCard title="Advertising">
                  <AreaOfAdvertising
                    onSelectAdvertising={(data: string[]) => (areaOfAdvertisingRef.current = data)}
                    intialValue={coach.areaOfAdvertising}
                  />
                </CustomCard>

                <CustomToolbar
                  {...formProps}
                  formName={FormGroupNames.coachesEdit}
                  customErrors={{
                    socials: socialLinksError ? "Error on Social media links" : "",
                    email: (enteredEmail.length === 0 && emailError) || (!!enteredEmail && emailError),
                  }}
                />
              </form>
            );
          }}
        />
      </FormGroupContextProvider>
    </FormWrapper>
  );
};

const CoachesEdit: FC<EditProps> = (props) => {
  const socialsRef = useRef<any[]>([]);
  const minReservationSlotRef = useRef("");
  const areaOfAdvertisingRef = useRef<string[]>([]);
  const minSlotRef = useRef("");
  const emailRef = useRef();

  return (
    <Edit
      {...props}
      component="section"
      transform={(values) => {
        values.phone = `+974${values.phone}`;
        values.whatsapp = `+974${values.whatsapp}`;
        values.socials = socialsRef.current;
        values.minReservationSlot = +minReservationSlotRef.current;
        values.areaOfAdvertising = areaOfAdvertisingRef.current ?? [];
        values.defaultSlotTime = +minSlotRef.current;
        values.email = emailRef.current;
        return values;
      }}
    >
      <CustomForm
        sanitizeEmptyValues={false}
        socialsRef={socialsRef}
        minReservationSlotRef={minReservationSlotRef}
        areaOfAdvertisingRef={areaOfAdvertisingRef}
        minSlotRef={minSlotRef}
        emailRef={emailRef}
      />
    </Edit>
  );
};

interface CustomEditProps {
  [key: string]: any;
}

export default CoachesEdit;
